/**
 * Collection facets (Dawn-style AJAX filtering).
 *
 * Intercepts filter/sort form submits and active-filter link clicks,
 * fetches updated section HTML via Shopify's Section Rendering API,
 * swaps filter UI + product grid without a full page reload, and
 * updates the browser URL with pushState.
 *
 * Markup: see `sections/template-collection.liquid`
 */
class CollectionFacets extends HTMLElement {
  constructor() {
    super();
    this._loading = false;
    this._mobileDialogOpen = false;
    this._closingFilters = false;
    this._onPopState = this._onPopState.bind(this);
    this._onClick = this._onClick.bind(this);
    this._onChange = this._onChange.bind(this);
    this._onSubmit = this._onSubmit.bind(this);
    this._onKeydown = this._onKeydown.bind(this);
    this._onFocusOut = this._onFocusOut.bind(this);
    this._onFilterToggle = this._onFilterToggle.bind(this);
    this._onDocumentClick = this._onDocumentClick.bind(this);
    this._onDocumentKeydown = this._onDocumentKeydown.bind(this);
    this._openDesktopFilterKey = null;
    this._mobileCountRequestId = 0;
    this._mobileSubmitLoadingCount = 0;
  }

  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.collectionUrl = this.dataset.collectionUrl || window.location.pathname;

    this.addEventListener('click', this._onClick);
    this.addEventListener('change', this._onChange);
    this.addEventListener('submit', this._onSubmit);
    this.addEventListener('keydown', this._onKeydown);
    this.addEventListener('focusout', this._onFocusOut);
    this.addEventListener('toggle', this._onFilterToggle);
    document.addEventListener('click', this._onDocumentClick);
    document.addEventListener('keydown', this._onDocumentKeydown);
    window.addEventListener('popstate', this._onPopState);

    if (!history.state?.collectionUrl) {
      history.replaceState(
        { ...(history.state || {}), collectionUrl: this._cleanUrl(window.location.href) },
        '',
        window.location.href
      );
    }

    this._cacheMobileDialogRefs();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this._onDocumentClick);
    document.removeEventListener('keydown', this._onDocumentKeydown);
    window.removeEventListener('popstate', this._onPopState);
    document.documentElement.classList.remove('overflow-hidden');
  }

  /* --------------------------------- Events ----------------------------- */

  _onClick(e) {
    const filterLink = e.target.closest('[data-collection-filter-link]');
    if (filterLink) {
      e.preventDefault();
      this._openDesktopFilterKey = null;
      this._renderFromUrl(this._urlWithoutPage(filterLink.href));
      return;
    }

    if (e.target.closest('[data-mobile-filters-open]')) {
      e.preventDefault();
      this._setMobileDialogOpen(true);
      return;
    }

    if (
      e.target.closest('[data-mobile-filters-close]') ||
      e.target.closest('[data-mobile-filters-backdrop]')
    ) {
      const dialog = e.target.closest('[data-mobile-filters-dialog]');
      if (dialog && dialog.contains(e.target)) {
        e.preventDefault();
        this._setMobileDialogOpen(false);
      }
    }
  }

  _onChange(e) {
    const target = e.target;
    if (!(target instanceof HTMLElement) || !('name' in target)) return;

    const sortForm = target.closest('[data-collection-sort-form]');
    if (sortForm) {
      this._openDesktopFilterKey = null;
      this._renderFromForm(sortForm);
      return;
    }

    const filterForm = target.closest('[data-collection-filters-form]');
    if (!filterForm) return;

    if (filterForm.closest('[data-mobile-filters-dialog]')) {
      this._updateMobileAccordionCounts(filterForm);
      this._scheduleMobileFilterCountPreview(filterForm);
      return;
    }

    if (!this._shouldAutoSubmit(filterForm, target)) return;

    this._rememberOpenDesktopFilter();
    const scroll = !(target instanceof HTMLInputElement && target.type === 'checkbox');
    this._renderFromForm(filterForm, { scroll });
  }

  _onSubmit(e) {
    const form = e.target;
    if (
      !form.matches('[data-collection-filters-form], [data-collection-sort-form]')
    ) {
      return;
    }

    e.preventDefault();
    const closeMobile = Boolean(form.closest('[data-mobile-filters-dialog]'));
    this._renderFromForm(form, { closeMobile });
  }

  _onKeydown(e) {
    if (e.key !== 'Enter') return;

    const target = e.target;
    if (!(target instanceof HTMLInputElement) || target.type !== 'number') return;

    const form = target.closest('[data-collection-filters-form]');
    if (!form) return;

    if (form.closest('[data-mobile-filters-dialog]')) {
      e.preventDefault();
      this._updateMobileAccordionCounts(form);
      this._scheduleMobileFilterCountPreview(form);
      return;
    }

    e.preventDefault();
    const closeMobile = Boolean(form.closest('[data-mobile-filters-dialog]'));
    this._renderFromForm(form, { closeMobile });
  }

  _onFocusOut(e) {
    const target = e.target;
    if (!(target instanceof HTMLInputElement) || target.type !== 'number') return;

    const form = target.closest('[data-collection-filters-form]');
    if (!form) return;

    if (form.closest('[data-mobile-filters-dialog]')) {
      this._updateMobileAccordionCounts(form);
      this._scheduleMobileFilterCountPreview(form, 500);
      return;
    }

    if (!this._shouldAutoSubmit(form, target)) return;

    clearTimeout(this._priceDebounce);
    this._priceDebounce = setTimeout(() => {
      this._rememberOpenDesktopFilter();
      this._renderFromForm(form, { scroll: false });
    }, 500);
  }

  _onFilterToggle(e) {
    if (this._closingFilters) return;

    const details = e.target;
    if (!(details instanceof HTMLDetailsElement)) return;
    if (
      !details.matches('.collection-filter-dropdown, .collection-mobile-filter-accordion')
    ) {
      return;
    }
    if (!details.open) return;

    const group =
      details.closest('[data-collection-filters-form]') ||
      details.closest('.collection-desktop-filters');
    if (!group) return;

    this._closeFilterPanels(group, details);
  }

  _closeFilterPanels(container, except = null) {
    if (!container) return;

    this._closingFilters = true;
    container
      .querySelectorAll(
        '.collection-filter-dropdown[open], .collection-mobile-filter-accordion[open]'
      )
      .forEach((el) => {
        if (el !== except) el.open = false;
      });
    this._closingFilters = false;
  }

  _rememberOpenDesktopFilter() {
    const open = this.querySelector('.collection-filter-dropdown[open]');
    this._openDesktopFilterKey = open?.dataset.filterKey || null;
  }

  _restoreOpenDesktopFilter() {
    if (!this._openDesktopFilterKey) return;

    const dropdown = this.querySelector(
      `.collection-filter-dropdown[data-filter-key="${CSS.escape(this._openDesktopFilterKey)}"]`
    );
    if (dropdown) dropdown.open = true;
  }

  _closeAllFilterPanels() {
    this._closeFilterPanels(this);
  }

  _onDocumentClick(e) {
    if (!this.isConnected) return;
    if (e.target.closest('.collection-filter-dropdown')) return;

    const desktop = this.querySelector('.collection-desktop-filters');
    if (!desktop?.querySelector('.collection-filter-dropdown[open]')) return;

    this._closeFilterPanels(desktop, null);
  }

  _onDocumentKeydown(e) {
    if (e.key !== 'Escape') return;

    if (this._mobileDialogOpen) {
      this._setMobileDialogOpen(false);
      return;
    }

    const desktop = this.querySelector('.collection-desktop-filters');
    if (desktop?.querySelector('.collection-filter-dropdown[open]')) {
      this._openDesktopFilterKey = null;
      this._closeFilterPanels(desktop, null);
    }
  }

  _onPopState(event) {
    const url = event.state?.collectionUrl || window.location.href;
    this._renderFromUrl(url, { pushState: false, scroll: false });
  }

  /* --------------------------------- Render ----------------------------- */

  async _renderFromForm(form, { closeMobile = false, scroll = true } = {}) {
    const params = this._paramsFromForm(form);
    const url = this._buildCollectionUrl(params);
    await this._renderFromUrl(url, { closeMobile, scroll });
  }

  async _renderFromUrl(url, { pushState = true, closeMobile = false, scroll = true } = {}) {
    if (this._loading) return;

    const mobileSubmitLoading = this._mobileDialogOpen;
    this._loading = true;
    this._setLoading(true);
    if (mobileSubmitLoading) this._setMobileSubmitLoading(true);

    try {
      const fetchUrl = this._buildSectionUrl(url);
      const res = await fetch(fetchUrl, { credentials: 'same-origin' });
      if (!res.ok) throw new Error(`Section fetch failed: ${res.status}`);

      const data = await res.json();
      const html = data[this.sectionId];
      if (!html) throw new Error('No section HTML in response');

      if (pushState) {
        const cleanUrl = this._cleanUrl(url);
        history.pushState({ collectionUrl: cleanUrl }, '', cleanUrl);
      }

      this._applySectionHTML(html);

      if (closeMobile) this._setMobileDialogOpen(false);

      if (scroll) this._scrollToResults();
      this._announceResults();
      this._notifySectionLoad();
    } catch (err) {
      console.error('[collection-facets]', err);
      window.location.href = this._cleanUrl(url);
    } finally {
      this._loading = false;
      this._setLoading(false);
      if (mobileSubmitLoading) this._setMobileSubmitLoading(false);
    }
  }

  _applySectionHTML(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const newFacets = doc.querySelector('collection-facets');
    if (!newFacets) return;

    const wasMobileOpen = this._mobileDialogOpen;
    const openFilterKey = this._openDesktopFilterKey;

    const uiTarget = this.querySelector('[data-collection-facets-ui]');
    const resultsTarget = this.querySelector('[data-collection-results]');
    const newUi = newFacets.querySelector('[data-collection-facets-ui]');
    const newResults = newFacets.querySelector('[data-collection-results]');

    if (uiTarget && newUi) uiTarget.innerHTML = newUi.innerHTML;
    if (resultsTarget && newResults) {
      resultsTarget.innerHTML = newResults.innerHTML;
      if (typeof window.initProductCards === 'function') {
        window.initProductCards(resultsTarget);
      }
    }

    this._cacheMobileDialogRefs();
    this._openDesktopFilterKey = openFilterKey;
    this._restoreOpenDesktopFilter();
    if (wasMobileOpen) this._setMobileDialogOpen(true);
  }

  /* --------------------------------- Helpers ---------------------------- */

  _scheduleMobileFilterCountPreview(form, delay = 300) {
    clearTimeout(this._mobileCountDebounce);
    this._mobileCountDebounce = setTimeout(() => {
      this._previewMobileFilterState(form);
    }, delay);
  }

  async _previewMobileFilterState(form) {
    this._mobilePreviewAbortController?.abort();
    const abortController = new AbortController();
    this._mobilePreviewAbortController = abortController;

    const requestId = ++this._mobileCountRequestId;
    this._setMobileSubmitLoading(true);

    try {
      const params = this._paramsFromForm(form);
      const url = this._buildCollectionUrl(params);
      const fetchUrl = this._buildSectionUrl(url);
      const res = await fetch(fetchUrl, {
        credentials: 'same-origin',
        signal: abortController.signal,
      });
      if (!res.ok) throw new Error(`Section fetch failed: ${res.status}`);

      const data = await res.json();
      const html = data[this.sectionId];
      if (!html || requestId !== this._mobileCountRequestId) return;

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const count =
        doc.querySelector('[data-mobile-filter-count]')?.textContent?.trim() ||
        doc.querySelector('[data-collection-product-count]')?.textContent?.trim()?.split(/\s/)[0];

      if (count != null && count !== '') {
        this._updateMobileFilterCount(count);
      }

      const activeFilters = doc.querySelector('[data-mobile-active-filters]');
      if (activeFilters) {
        this._updateMobileActiveFilters(activeFilters.innerHTML);
      }

      this._syncMobileFilterValuesFromDoc(doc, form);
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('[collection-facets] mobile filter preview failed', err);
    } finally {
      this._setMobileSubmitLoading(false);
    }
  }

  _setMobileSubmitLoading(isLoading) {
    if (isLoading) {
      this._mobileSubmitLoadingCount += 1;
    } else {
      this._mobileSubmitLoadingCount = Math.max(0, this._mobileSubmitLoadingCount - 1);
    }

    this._applyMobileSubmitLoadingUI();
  }

  _resetMobileSubmitLoading() {
    this._mobileSubmitLoadingCount = 0;
    this._applyMobileSubmitLoadingUI();
  }

  _applyMobileSubmitLoadingUI() {
    const btn = this.querySelector('[data-mobile-filter-submit]');
    if (!btn) return;

    const show = this._mobileSubmitLoadingCount > 0;
    const spinner = btn.querySelector('[data-mobile-filter-submit-spinner]');
    const label = btn.querySelector('[data-mobile-filter-submit-label]');

    btn.disabled = show;
    btn.setAttribute('aria-busy', show ? 'true' : 'false');
    if (spinner) spinner.hidden = !show;
    if (label) label.classList.toggle('opacity-0', show);
  }

  _updateMobileFilterCount(count) {
    const countEl = this.querySelector('[data-mobile-filter-count]');
    if (countEl) countEl.textContent = count;
  }

  _updateMobileActiveFilters(html) {
    const container = this.querySelector('[data-mobile-active-filters]');
    if (!container) return;

    const trimmed = html.trim();
    container.innerHTML = trimmed;
    container.classList.toggle('hidden', trimmed === '');
  }

  _updateMobileAccordionCounts(form) {
    form.querySelectorAll('.collection-mobile-filter-accordion').forEach((details) => {
      const labelEl = details.querySelector('[data-filter-label]');
      if (!labelEl) return;

      const baseLabel = labelEl.dataset.filterLabel || labelEl.textContent.replace(/\s*\(\d+\)$/, '');
      let count = details.querySelectorAll(
        'input[type="checkbox"]:checked:not([data-color-filter-member])'
      ).length;

      const hasPriceValue = Array.from(details.querySelectorAll('input[type="number"]')).some(
        (input) => input.value !== ''
      );
      if (hasPriceValue) count = Math.max(count, 1);

      labelEl.textContent = count > 0 ? `${baseLabel} (${count})` : baseLabel;
    });
  }

  _syncMobileFilterValuesFromDoc(doc, form) {
    const remoteForm = doc.getElementById('CollectionFiltersFormMobile');
    if (!remoteForm) return;

    const openKeys = new Set(
      [...form.querySelectorAll('.collection-mobile-filter-accordion[open][data-filter-key]')].map(
        (details) => details.dataset.filterKey
      )
    );

    const localAccordions = form.querySelector('[data-mobile-filter-accordions]');
    const remoteAccordions = remoteForm.querySelector('[data-mobile-filter-accordions]');
    if (!localAccordions || !remoteAccordions) return;

    localAccordions.innerHTML = remoteAccordions.innerHTML;

    localAccordions
      .querySelectorAll('.collection-mobile-filter-accordion[data-filter-key]')
      .forEach((details) => {
        if (openKeys.has(details.dataset.filterKey)) {
          details.open = true;
        }
      });
  }

  _shouldAutoSubmit(form, target) {
    if (form.getAttribute('data-auto-submit') === 'false') return false;
    if (form.closest('[data-mobile-filters-dialog]')) return false;
    if (target instanceof HTMLInputElement && target.type === 'number') return false;
    return true;
  }

  _paramsFromForm(form) {
    form.querySelectorAll('[data-color-filter-group]').forEach((group) => {
      const master = group.querySelector('[data-color-filter-master]');
      if (!master) return;
      group.querySelectorAll('[data-color-filter-member]').forEach((member) => {
        member.checked = master.checked;
      });
    });

    const formData = new FormData(form);
    const params = new URLSearchParams();

    for (const [key, value] of formData.entries()) {
      if (value !== '') params.append(key, value);
    }

    params.delete('page');

    return params;
  }

  _buildCollectionUrl(params) {
    const query = params.toString();
    return query ? `${this.collectionUrl}?${query}` : this.collectionUrl;
  }

  _buildSectionUrl(url) {
    const parsed = new URL(url, window.location.origin);
    parsed.searchParams.set('sections', this.sectionId);
    return parsed.toString();
  }

  _cleanUrl(url) {
    const parsed = new URL(url, window.location.origin);
    parsed.searchParams.delete('sections');
    return `${parsed.pathname}${parsed.search}`;
  }

  _urlWithoutPage(url) {
    const parsed = new URL(url, window.location.origin);
    parsed.searchParams.delete('sections');
    parsed.searchParams.delete('page');
    return `${parsed.pathname}${parsed.search}`;
  }

  _setLoading(isLoading) {
    this.toggleAttribute('data-loading', isLoading);
    const results = this.querySelector('[data-collection-results]');
    if (results) results.setAttribute('aria-busy', isLoading ? 'true' : 'false');
  }

  _scrollToResults() {
    const heading = this.querySelector('#products-heading');
    const target = heading?.closest('section') || this.querySelector('[data-collection-results]');
    if (!target) return;

    const offset = 80;
    const top = target.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  }

  _announceResults() {
    const status = this.querySelector('[data-collection-facets-status]');
    const countEl = this.querySelector('[data-collection-product-count]');
    if (!status) return;

    const count = countEl?.textContent?.trim() || '';
    status.textContent = count
      ? `Producten bijgewerkt. ${count}.`
      : 'Producten bijgewerkt.';
  }

  _notifySectionLoad() {
    const sectionEl = document.getElementById(`shopify-section-${this.sectionId}`);
    if (sectionEl) {
      sectionEl.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true }));
    }
  }

  /* --------------------------------- Mobile dialog ---------------------- */

  _cacheMobileDialogRefs() {
    this._mobileDialog = this.querySelector('[data-mobile-filters-dialog]');
    this._mobileOpenBtn = this.querySelector('[data-mobile-filters-open]');
    this._mobileCloseBtn = this._mobileDialog?.querySelector('[data-mobile-filters-close]');
  }

  _setMobileDialogOpen(isOpen) {
    this._mobileDialogOpen = isOpen;
    const dialog = this._mobileDialog;
    if (!dialog) return;

    dialog.classList.toggle('hidden', !isOpen);
    dialog.setAttribute('aria-hidden', isOpen ? 'false' : 'true');

    this.querySelectorAll('[data-mobile-filters-open]').forEach((btn) => {
      btn.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    });
    if (this._mobileCloseBtn) {
      this._mobileCloseBtn.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    }

    document.documentElement.classList.toggle('overflow-hidden', isOpen);

    if (isOpen && this._mobileCloseBtn) {
      this._mobileCloseBtn.focus();
    }

    if (isOpen) {
      this._resetMobileSubmitLoading();
    }
  }
}

if (!customElements.get('collection-facets')) {
  customElements.define('collection-facets', CollectionFacets);
}
